import { useState, useEffect, useRef, useCallback } from 'react';

export function usePollingRecords({
                                      enabled = true,
                                      loadRecords,
                                      pollRecord,
                                      shouldPollStatus,
                                      pollInterval = 5000,
                                      filterOnLoad = null,
                                      idField = 'id',
                                  }) {
    const [records, setRecords] = useState({});
    const [loading, setLoading] = useState(false);
    const pollIntervalRef = useRef(null);

    const updateRecord = useCallback((recordId, updates) => {
        setRecords((prev) => ({
            ...prev,
            [recordId]: {
                ...prev[recordId],
                ...updates,
            },
        }));
    }, []);

    const removeRecord = useCallback((recordId) => {
        setRecords((prev) => {
            const { [recordId]: removed, ...remaining } = prev;
            return remaining;
        });
    }, []);

    // Move pollSingleRecord BEFORE startPolling
    const pollSingleRecord = useCallback(
        async (recordId) => {
            if (!enabled) return;

            try {
                const recordData = await pollRecord(recordId);

                if (!recordData) return;

                if (filterOnLoad && !filterOnLoad(recordData)) {
                    removeRecord(recordId);
                } else {
                    updateRecord(recordId, { ...recordData, isLoading: false });
                }
            } catch (err) {
                console.error('Failed to poll record', err);
            }
        },
        [enabled, pollRecord, filterOnLoad, updateRecord, removeRecord]
    );

    const startPolling = useCallback(() => {
        if (!enabled) return;

        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }

        pollIntervalRef.current = setInterval(() => {
            // Access current state via callback to avoid stale closure
            setRecords((currentRecords) => {
                const recordsToPoll = Object.values(currentRecords).filter((record) =>
                    shouldPollStatus(record.status)
                );

                if (recordsToPoll.length === 0) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                    return currentRecords; // Return unchanged state
                }

                recordsToPoll.forEach((record) => {
                    pollSingleRecord(record[idField]);
                });

                return currentRecords; // Return unchanged state
            });
        }, pollInterval);
    }, [enabled, shouldPollStatus, pollInterval, idField, pollSingleRecord]); // Remove 'records' from deps

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await loadRecords();

            const recordsMap = {};
            data.forEach((record) => {
                if (!filterOnLoad || filterOnLoad(record)) {
                    recordsMap[record[idField]] = record;
                }
            });

            setRecords(recordsMap);
            if (enabled) {
                const hasRecordsToPoll = data.some((record) => shouldPollStatus(record.status));
                if (hasRecordsToPoll) {
                    startPolling();
                }
            }
        } catch (err) {
            console.error('Failed to load records', err);
        } finally {
            setLoading(false);
        }
    }, [enabled, loadRecords, filterOnLoad, shouldPollStatus, startPolling, idField]);

    useEffect(() => {
        load();

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!enabled && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [enabled]);

    return {
        records,
        loading,
        updateRecord,
        removeRecord,
        startPolling,
        reload: load,
    };
}